import { useEffect, useRef, useState } from "react";
import { LOGO_URL } from "@/lib/api";
import { X, CheckSquare, Square, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function SignaturePad({ onCapture }) {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    const pos = getPos(e);
    lastPos.current = pos;
    const ctx = canvasRef.current.getContext("2d");
    ctx.fillStyle = "#032352";
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.5, 0, Math.PI * 2);
    ctx.fill();
    setEmpty(false);
  };

  const move = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.strokeStyle = "#032352";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const stop = () => {
    if (!isDrawing.current) return;
    isDrawing.current = false;
    onCapture(canvasRef.current.toDataURL("image/png"));
  };

  const clear = (e) => {
    e.stopPropagation();
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setEmpty(true);
    onCapture(null);
  };

  return (
    <div className="relative border-2 border-[#032352] rounded-lg bg-white" style={{ height: 160 }}>
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        className="w-full h-full rounded-lg"
        style={{ display: "block", touchAction: "none", cursor: "crosshair" }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={stop}
        onMouseLeave={stop}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={stop}
      />
      {empty && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-slate-400 text-sm select-none">Draw signature here</span>
        </div>
      )}
      {!empty && (
        <button
          type="button"
          onClick={clear}
          data-testid="clear-signature-btn"
          className="absolute top-2 right-2 text-xs bg-white border border-slate-200 rounded px-2 py-1 text-slate-500 hover:text-red-500 transition-colors z-10"
        >
          Clear
        </button>
      )}
    </div>
  );
}

export default function SigningScreen({ invoice, onCancel, onComplete }) {
  const today = new Date();
  const todayISO = today.toISOString().slice(0, 10);
  const todayDisplay = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;

  const [items, setItems] = useState(
    (invoice?.line_items || []).map((it) => ({ ...it, confirmed: true }))
  );
  const [signedBy, setSignedBy] = useState("");
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const toggleItem = (idx) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, confirmed: !it.confirmed } : it)));

  const canSubmit = signedBy.trim().length > 0 && signature !== null;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onComplete({
        signed_by: signedBy.trim(),
        signed_at: todayISO,
        signature_base64: signature,
        items_confirmed: items,
      });
    } catch {
      setSubmitting(false);
    }
  };

  const co = invoice?.company_details || {};

  return (
    <div className="fixed inset-0 bg-white z-50 overflow-y-auto" data-testid="signing-screen">
      {/* Sticky top bar */}
      <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between z-10 shadow-sm">
        <img src={LOGO_URL} alt="J&L" className="h-10 object-contain" crossOrigin="anonymous" />
        <div className="text-center">
          <p className="font-heading font-bold text-[#032352] text-lg leading-tight">DELIVERY NOTE</p>
          <p className="font-mono-num text-sm text-[#5B5C5F]">{invoice?.invoice_number}</p>
        </div>
        <button
          onClick={onCancel}
          data-testid="signing-cancel-btn"
          className="p-2 text-[#5B5C5F] hover:text-[#032352] transition-colors rounded-md hover:bg-slate-100"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Delivery info */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1">Deliver To</p>
            <p className="font-semibold text-[#032352]">{invoice?.customer_name}</p>
            {invoice?.customer_address && (
              <p className="text-[#5B5C5F] whitespace-pre-line text-xs mt-0.5">{invoice.customer_address}</p>
            )}
          </div>
          <div className="text-right">
            {invoice?.order_number && (
              <div>
                <p className="text-xs uppercase tracking-wider text-[#5B5C5F]">Order No</p>
                <p className="font-medium text-[#032352]">{invoice.order_number}</p>
              </div>
            )}
          </div>
        </div>

        {/* Items checklist */}
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1">Items Being Delivered</p>
          <p className="text-xs text-slate-400 mb-3">Tap to untick any items NOT included in this delivery</p>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {items.map((it, i) => (
              <button
                key={i}
                data-testid={`sign-item-${i}`}
                onClick={() => toggleItem(i)}
                className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                  it.confirmed ? "bg-white hover:bg-slate-50" : "bg-red-50 hover:bg-red-100"
                }`}
              >
                {it.confirmed ? (
                  <CheckSquare className="w-5 h-5 text-emerald-500 shrink-0" />
                ) : (
                  <Square className="w-5 h-5 text-slate-300 shrink-0" />
                )}
                <span className="flex-1 text-[#032352] text-sm font-medium">
                  {it.title}
                  {it.variant ? <span className="text-[#5B5C5F] font-normal"> — {it.variant}</span> : null}
                </span>
                <span className="font-mono-num text-[#032352] text-sm font-semibold shrink-0">
                  ×{it.quantity}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="border-t-2 border-[#032352]/20" />

        {/* Signature section */}
        <div className="space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-1">Date of Delivery</p>
            <p className="text-[#032352] font-semibold text-lg" data-testid="sign-date">{todayDisplay}</p>
          </div>

          <div>
            <Label htmlFor="signed-by-input" className="text-xs uppercase tracking-wider text-[#5B5C5F]">
              Received By (Print Name)
            </Label>
            <Input
              id="signed-by-input"
              data-testid="signed-by-input"
              value={signedBy}
              onChange={(e) => setSignedBy(e.target.value)}
              placeholder="Full name of person accepting delivery"
              className="mt-1 text-base h-12"
              autoComplete="off"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5B5C5F] mb-2 block">Signature</Label>
            <SignaturePad onCapture={setSignature} />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pb-10">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1"
            data-testid="sign-cancel-btn"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            data-testid="sign-submit-btn"
            className="flex-1 bg-[#032352] hover:bg-[#04347A] disabled:opacity-40"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            {submitting ? "Saving…" : "Complete & Submit"}
          </Button>
        </div>
      </div>
    </div>
  );
}
